import {
  calculateMechanicalAvailabilityPercentage,
  calculateRuntimePercentage,
} from "@/lib/downtime-data-utils.js";
import {
  computeSilentDurationMs,
  formatPercentForCsv,
  getSeverity,
  silentMsToDays,
} from "@/lib/no-data-utils.js";
import { formatDateForCSV, formatDateTimeWithSeconds } from "@/lib/datetime-utils.js";
import { Temporal } from "@js-temporal/polyfill";

/**
 * Escapes a value for CSV output. Quotes when the string contains a comma,
 * newline, or quote — or always when `alwaysQuote` is true (helps Excel keep
 * text fields as text).
 * @param {unknown} value
 * @param {boolean} [alwaysQuote=false]
 * @returns {unknown}
 */
export const escapeForCSV = (value, alwaysQuote = false) => {
  if (!alwaysQuote && typeof value !== "string") {
    return value;
  }

  const text = alwaysQuote ? String(value ?? "") : /** @type {string} */ (value);
  if (
    alwaysQuote ||
    text.includes(",") ||
    text.includes("\n") ||
    text.includes('"')
  ) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

/**
 * Parses a single CSV line, respecting double-quoted fields.
 * @param {string} line
 * @returns {string[]}
 */
export function parseCsvLine(line) {
  const fields = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      fields.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  fields.push(current);
  return fields;
}

/**
 * Normalizes a CSV header for column lookup (letters/digits only, lowercased).
 * @param {string} header
 * @returns {string}
 */
export function normalizeCsvHeader(header) {
  return String(header ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

/**
 * Finds a column index by any of the accepted normalized header names.
 * @param {string[]} normalizedHeaders
 * @param {string[]} aliases
 * @returns {number}
 */
export function findCsvColumnIndex(normalizedHeaders, aliases) {
  for (const alias of aliases) {
    const idx = normalizedHeaders.indexOf(alias);
    if (idx >= 0) return idx;
  }
  return -1;
}

/**
 * Strips Excel's leading apostrophe text-marker from a cell value.
 * @param {string} value
 * @returns {string}
 */
export function stripExcelTextMarker(value) {
  const trimmed = String(value ?? "").trim();
  return trimmed.startsWith("'") ? trimmed.slice(1).trim() : trimmed;
}

function createUtf8CsvBlob(csvBody) {
  return new Blob(["\uFEFF" + csvBody], {
    type: "text/csv;charset=utf-8",
  });
}

function buildCsvBody(rows) {
  return rows.map((row) => row.join(",")).join("\n");
}

function downloadCsvFile(contents, fileName) {
  const blob = createUtf8CsvBlob(contents);
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${fileName}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function zonedRangeFromIsoDates(dates) {
  const timeZone = Temporal.Now.timeZoneId();
  return {
    start: Temporal.Instant.from(dates.startDate).toZonedDateTimeISO(timeZone),
    end: Temporal.Instant.from(dates.endDate).toZonedDateTimeISO(timeZone),
  };
}

function performanceCsvFileName(start, end) {
  return `Asset Performance - ${start.toPlainDate()} - ${end.toPlainDate()}`;
}

function downtimeEventsCsvFileName(start, end) {
  return `Downtime Events - ${start.toPlainDate()} - ${end.toPlainDate()}`;
}

function toZonedDateTime(isoString) {
  return Temporal.Instant.from(isoString).toZonedDateTimeISO(
    Temporal.Now.timeZoneId()
  );
}

function formatMechanicalType(mechanical) {
  if (mechanical === true) return "Mechanical";
  if (mechanical === false) return "NonMechanical";
  return "";
}

function getLatestDowntimeEventAlarm(events) {
  if (!events?.length) return "";
  const sorted = [...events].sort((a, b) =>
    Temporal.Instant.compare(
      Temporal.Instant.from(b.dateTime),
      Temporal.Instant.from(a.dateTime)
    )
  );
  return escapeForCSV(sorted[0].relatedAlarm);
}

function formatCountForCsv(count) {
  if (count == null || !Number.isFinite(count)) return "";
  return String(count);
}

function transformDowntimeEventsToCSVData(filteredData) {
  const headers = [
    "Asset Number",
    "Lease Name",
    "Location",
    "Vendor",
    "Downtime Reason",
    "Downtime Length (Hours)",
    "Downtime Start Date",
    "Downtime End Date",
    "Mechanical/Non-Mechanical",
  ];

  if (!Array.isArray(filteredData)) {
    return buildCsvBody([headers]);
  }

  const rows = [headers];

  for (const assetData of filteredData) {
    if (!assetData?.events?.length) continue;

    for (const event of assetData.events) {
      if (!event || typeof event !== "object") continue;

      rows.push([
        escapeForCSV(assetData.assetNumber),
        escapeForCSV(assetData.assetLease) || "",
        escapeForCSV(assetData.assetLocation) || "",
        escapeForCSV(assetData.vendor) || "",
        escapeForCSV(event.relatedAlarm) || "",
        (event.duration / 60).toFixed(2) || "",
        formatDateForCSV(
          event.dateTime
            ? toZonedDateTime(event.dateTime)
            : Temporal.Now.zonedDateTimeISO()
        ),
        !event.active ? formatDateForCSV(toZonedDateTime(event.dateTimeEnd)) : "",
        formatMechanicalType(event.mechanical),
      ]);
    }
  }

  return buildCsvBody(rows);
}

function transformFilteredAssetsToAssetPerformanceCSVData(
  filteredAssets,
  startDate,
  endDate
) {
  const headers = [
    "Asset Number",
    "Lease Name",
    "Location",
    "Vendor",
    "Status",
    "Downtime (Hours)",
    "Runtime %",
    "Mechanical Availability %",
    "Communications Availability %",
    "Last Downtime Reason",
    "Events",
  ];

  if (!Array.isArray(filteredAssets)) {
    return buildCsvBody([headers]);
  }

  const rangeStart = new Date(startDate.epochMilliseconds);
  const rangeEnd = new Date(endDate.epochMilliseconds);
  const rows = [headers];

  for (const asset of filteredAssets) {
    rows.push([
      escapeForCSV(asset.assetNumber),
      escapeForCSV(asset.assetLease) || "",
      escapeForCSV(asset.assetLocation) || "",
      escapeForCSV(asset.vendor) || "",
      asset.status || "",
      asset.totalDTHours.toFixed(2) || "",
      formatPercentForCsv(
        calculateRuntimePercentage(asset.totalDTHours, rangeStart, rangeEnd)
      ),
      formatPercentForCsv(
        calculateMechanicalAvailabilityPercentage(
          asset.mechanicalDTHours,
          rangeStart,
          rangeEnd
        )
      ),
      formatPercentForCsv(asset.communicationsAvailabilityPercent),
      getLatestDowntimeEventAlarm(asset.events),
      asset.eventCount || "",
    ]);
  }

  return buildCsvBody(rows);
}

export const downloadDowntimeEventsPerAssets = async (
  dates,
  filteredCompressors
) => {
  const { start, end } = zonedRangeFromIsoDates(dates);
  downloadCsvFile(
    transformDowntimeEventsToCSVData(filteredCompressors),
    downtimeEventsCsvFileName(start, end)
  );
};

export const downloadAssetPerformance = async (dates, filteredCompressors) => {
  const { start, end } = zonedRangeFromIsoDates(dates);
  downloadCsvFile(
    transformFilteredAssetsToAssetPerformanceCSVData(
      filteredCompressors,
      start,
      end
    ),
    performanceCsvFileName(start, end)
  );
};

export const downloadFleetTableCSV = async (visibleColumns, assets) => {
  const now = Temporal.Now.zonedDateTimeISO();
  const dateStr =
    `${now.year.toString().padStart(4, "0")}-${now.month.toString().padStart(2, "0")}-${now.day.toString().padStart(2, "0")}`;

  const headers = visibleColumns.map((col) => escapeForCSV(col.label));
  const rows = assets.map((asset) =>
    visibleColumns.map((col) => {
      const value = col.csvValue ? col.csvValue(asset) : (asset[col.key] ?? "");
      return escapeForCSV(value);
    })
  );

  downloadCsvFile(buildCsvBody([headers, ...rows]), `Fleet Status - ${dateStr}`);
};

export const downloadTrendAnalysis = async (assetNumber, trendData) => {
  const headers = ["TagName", "ReportingDate", "MeasurementValue", "Unit"];
  const rows = [headers];

  for (const tagData of trendData) {
    const tagName = tagData.param.parameter;
    const tagUnit = tagData.param.unit || "";
    if (tagName == null) continue;

    for (const dataEntry of tagData.data) {
      rows.push([
        escapeForCSV(tagName),
        formatDateForCSV(toZonedDateTime(dataEntry.timestamp)),
        escapeForCSV(dataEntry.value),
        escapeForCSV(tagUnit),
      ]);
    }
  }

  const dateTime = Temporal.Now.zonedDateTimeISO();
  const fileName =
    `TrendAnalysis-${escapeForCSV(assetNumber)}-${dateTime.year.toString().padStart(4, "0")}${dateTime.month.toString().padStart(2, "0")}${dateTime.day.toString().padStart(2, "0")}${dateTime.hour.toString().padStart(2, "0")}${dateTime.minute.toString().padStart(2, "0")}${dateTime.second.toString().padStart(2, "0")}`;

  downloadCsvFile(buildCsvBody(rows), fileName);
};

export const downloadNoDataReportCSV = async (rows, options = {}) => {
  const now = options.now ?? new Date();
  const headers = [
    "Asset Number",
    "Vendor",
    "Location",
    "Customer",
    "Telemetry",
    "Last Updated",
    "No Data Event Count",
    "Communications Availability (%)",
    "Silent Duration (days)",
    "Severity",
  ];

  const csvRows = (rows || []).map((asset) => {
    const silentMs = computeSilentDurationMs(asset, now);
    const severity = getSeverity(silentMs);

    return [
      escapeForCSV(asset.assetNumber ?? ""),
      escapeForCSV(asset.vendor ?? ""),
      escapeForCSV(asset.location ?? ""),
      escapeForCSV(asset.customer ?? asset.lease ?? ""),
      escapeForCSV(asset.telemetrySource ?? ""),
      escapeForCSV(formatDateTimeWithSeconds(asset.lastUpdated)),
      formatCountForCsv(asset.noDataEventCount),
      formatPercentForCsv(asset.availabilityPercent, 1),
      silentMsToDays(silentMs),
      escapeForCSV(severity.label),
    ];
  });

  downloadCsvFile(
    buildCsvBody([headers, ...csvRows]),
    `No-Data Report - ${now.toISOString().slice(0, 10)}`
  );
};

/**
 * Local date + HHMM stamp for unique CSV filenames (e.g. 2026-07-20_1345).
 * @returns {string}
 */
function csvDownloadTimestamp() {
  const now = Temporal.Now.zonedDateTimeISO();
  const hh = now.hour.toString().padStart(2, "0");
  const mm = now.minute.toString().padStart(2, "0");
  return `${now.toPlainDate()}_${hh}${mm}`;
}

/**
 * Downloads unmapped downtime classification rows as CSV for bulk classify-and-reimport.
 * @param {{ Vendor?: string, Message?: string, DowntimeEventCount?: number, DowntimeHoursCount?: number, IsMech?: boolean|null }[]} rows
 */
export const downloadUnmappedClassificationsCSV = (rows) => {
  const headers = [
    "Vendor",
    "Message",
    "DowntimeEventCount",
    "DowntimeHoursCount",
    "IsMech",
  ];

  const csvRows = (rows || []).map((row) => {
    let isMech = "";
    if (row.IsMech === true) isMech = "Mech";
    else if (row.IsMech === false) isMech = "Non-Mech";

    return [
      escapeForCSV(row.Vendor, true),
      escapeForCSV(row.Message, true),
      row.DowntimeEventCount ?? "",
      row.DowntimeHoursCount ?? "",
      escapeForCSV(isMech, true),
    ];
  });

  downloadCsvFile(
    buildCsvBody([headers, ...csvRows]),
    `Unmapped Classifications - ${csvDownloadTimestamp()}`
  );
};
